import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useState, useRef } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import type { HeadingRefs } from '@models/Toc';
import { LinkIcon } from '@epic-ui/icons';
import { useTranslationResponse } from '@isomorphic/hooks/use-translation-response';
import { useTOCContext } from '@contexts/ToC';
import { useScrollIntoView } from '@client/hooks/use-scroll-into-view';
import { useActiveAnchorId } from '@layout/components/TOC/hooks/use-active-heading-id';
import { useAccordionHeaderContext } from '@mdx-cmp/Accordion/context/AccordionHeader';
import { useAnchorLink } from './hooks/use-anchor-link';
import { StyledAnchorHeading } from './AnchorHeading.style';

interface AnchorHeadingProps {
  level: number;
  children: ReactNode;
  id: string;
}

export const AnchorHeading: FC<AnchorHeadingProps> = ({ level, children, ...restProps }) => {
  const CustomHeadingTag = `h${level}` as any;

  const refAnchor = useRef<HTMLElement>();
  const [copied, setCopied] = useState<boolean>(false);
  const { tableOfContents, setHeadingRefs } = useTOCContext();
  const copyText: string = useTranslationResponse('CopySectionLink');
  const copySuccess: string = useTranslationResponse('CopiedToClipBoard');

  const accordionHeading = useAccordionHeaderContext();
  const link: string = useAnchorLink(tableOfContents, children, restProps.id, accordionHeading);

  const handleCopy = useCallback(() => {
    setCopied(true);
  }, []);

  const { activeAnchorId } = useActiveAnchorId();

  useScrollIntoView(refAnchor, activeAnchorId);

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 1000);
    }
  }, [copied]);

  useEffect(() => {
    if (refAnchor.current) {
      setHeadingRefs((prevState: HeadingRefs) =>
        [...prevState, refAnchor].filter(
          (ref, index, list) => ref.current && index === list.findIndex((r) => r.current?.id === ref.current?.id)
        )
      );
    }
  }, [refAnchor, setHeadingRefs]);

  return (
    <StyledAnchorHeading className="anchor_heading">
      <CustomHeadingTag {...restProps} ref={refAnchor}>
        {children}
      </CustomHeadingTag>
      <CopyToClipboard text={link} onCopy={handleCopy}>
        <Tooltip title={copied ? copySuccess : copyText}>
          <IconButton>
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </CopyToClipboard>
    </StyledAnchorHeading>
  );
};
